import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  FileText, Filter, RefreshCw, Download 
} from 'lucide-react';
import {
  Card, CardContent 
} from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { PurchaseOrderCard } from '@/features/orders';
import {
  usePurchaseOrders, useUpdatePOStatus 
} from '@/hooks/use-purchase-orders';
import {
  type POStatus, PO_STATUS_LABELS 
} from '@/types/orders';
import { toast } from 'sonner';

const STATUS_FILTERS: (POStatus | 'all')[] = ['all', 'sent', 'acknowledged', 'shipped', 'delivered'];

export function PurchaseOrdersPage() {
  const navigate = useNavigate();
  const [statusFilter, setStatusFilter] = useState<POStatus | 'all'>('all');
  const {
    data, isLoading, error, refetch 
  } = usePurchaseOrders(
    statusFilter === 'all' ? undefined : { status: statusFilter }
  );
  const updateStatus = useUpdatePOStatus();

  const handleAcknowledge = (poId: string) => {
    updateStatus.mutate({
      poId,
      action: 'acknowledge' 
    });
  };

  const handleMarkShipped = (poId: string) => {
    updateStatus.mutate({
      poId,
      action: 'ship' 
    });
  };

  const handleViewDetails = (poId: string) => {
    navigate(`/orders/purchase-orders/${poId}`);
  };

  if (isLoading) {
    return (
      <div className="space-y-6">
        <PageHeader />
        <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
          {[1, 2, 3].map((i) => (
            <Card key={i} className="h-64 animate-pulse bg-muted" />
          ))}
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="space-y-6">
        <PageHeader />
        <Card>
          <CardContent className="flex flex-col items-center justify-center py-12">
            <p className="text-muted-foreground mb-4">Failed to load purchase orders</p>
            <Button onClick={() => refetch()}>
              <RefreshCw className="h-4 w-4 mr-2" />
              Retry
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  const orders = data?.data ?? [];

  const handleExport = () => {
    if (orders.length === 0) {
      toast.info('No data to export');
      return;
    }
    const exportData = orders.map((o) => ({
      poNumber: o.poNumber,
      status: o.status,
      deliverySite: o.deliverySiteName,
      expectedDeliveryDate: o.expectedDeliveryDate,
      total: o.total,
      currency: o.currency,
      lineCount: o.lines.length,
      createdAt: o.createdAt,
    }));
    const headers = Object.keys(exportData[0]);
    const csvContent = [
      headers.join(','),
      ...exportData.map((row) =>
        headers.map((h) => {
          const val = row[h as keyof typeof row];
          if (typeof val === 'string' && (val.includes(',') || val.includes('"'))) {
            return `"${val.replaceAll('"', '""')}"`;
          }
          return String(val ?? '');
        }).join(',')
      ),
    ].join('\n');
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = 'purchase-orders.csv';
    link.click();
    URL.revokeObjectURL(url);
    toast.success(`Exported ${orders.length} purchase orders`);
  };

  return (
    <div className="space-y-6">
      <PageHeader />

      <div className="flex items-center gap-2 flex-wrap">
        <Filter className="h-4 w-4 text-muted-foreground" />
        {STATUS_FILTERS.map((status) => (
          <Button
            key={status}
            variant={statusFilter === status ? 'default' : 'outline'}
            size="sm"
            onClick={() => setStatusFilter(status)}
          >
            {status === 'all' ? 'All Orders' : PO_STATUS_LABELS[status]}
          </Button>
        ))}
        <div className="flex-1" />
        <Button size="sm" variant="outline" onClick={handleExport}>
          <Download className="mr-2 h-4 w-4" />
          Export
        </Button>
      </div>

      {orders.length === 0 ? (
        <Card>
          <CardContent className="flex flex-col items-center justify-center py-12">
            <FileText className="h-12 w-12 text-muted-foreground mb-4" />
            <p className="text-lg font-medium">No purchase orders</p>
            <p className="text-muted-foreground">
              {statusFilter === 'all'
                ? 'You have no purchase orders yet'
                : `No orders with status "${PO_STATUS_LABELS[statusFilter]}"`}
            </p>
          </CardContent>
        </Card>
      ) : (
        <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
          {orders.map((order) => (
            <PurchaseOrderCard
              key={order.poId}
              order={order}
              onAcknowledge={handleAcknowledge}
              onMarkShipped={handleMarkShipped}
              onViewDetails={handleViewDetails}
            />
          ))}
        </div>
      )}
    </div>
  );
}

function PageHeader() {
  return (
    <div>
      <h1 className="text-2xl font-semibold">Purchase Orders</h1>
      <p className="text-muted-foreground">
        View and manage purchase orders from your customers
      </p>
    </div>
  );
}
